import { apiClient } from './api';
import type { Cover, DeckLayouts, DraftStart, DraftStatus, GuideList, GuideUpload, TemplateId } from '../types/deck';

const relName = (f: File) => (f as any).webkitRelativePath || f.name;

const save = (blob: Blob, name: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

/**
 * 문서 관리 메뉴 · 화면을 여는가. false 면 키 · 연결이 돼 있어도 메뉴는 회색 「준비중」, 화면은 안내만 보인다.
 * 2026-10-30 운영에도 열었다. 다시 닫으려면 `window.location.hostname !== 'admin.grkcon.com'` 으로 되돌린다.
 */
export const DECK_OPEN = true;

type DeckOut = { layout: string; texts: Record<string, string> };
type DeckPic = { slide: number; key: string; file: File };
const deckForm = (template: TemplateId, title: string, slides: DeckOut[], images: DeckPic[]) => {
  const form = new FormData();
  form.append('deck', JSON.stringify({ template, title, slides }));
  for (const im of images) form.append(`img:${im.slide}:${im.key}`, im.file, im.file.name);
  return form;
};

/** 문서 · PPT (GRK_admin#113) — 본체는 서버 공용 grk-deck */
export const deckApi = {
  status: () => apiClient.get<{ ready: boolean }>('/deck/status'),
  layouts: (template: TemplateId) => apiClient.get<DeckLayouts>(`/deck/layouts?template=${template}`),
  guides: (template: TemplateId) => apiClient.get<GuideList>(`/deck/guides?template=${template}`),

  downloadGuide: async (template: TemplateId, version: string) => {
    save(await apiClient.getBlob(`/deck/guides/${encodeURIComponent(version)}?template=${template}`), `${template}_PPT_템플릿_가이드_${version}.md`);
  },
  guideText: async (template: TemplateId, version: string) =>
    (await apiClient.getBlob(`/deck/guides/${encodeURIComponent(version)}?template=${template}`)).text(),

  uploadGuide: (template: TemplateId, file: File, note: string) => {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('note', note);
    form.append('template', template);
    return apiClient.upload<GuideUpload>('/deck/guides', form);
  },

  startDraft: (files: File[], opts: { template: TemplateId; title: string; slides: number; cover: Cover; guide: string }) => {
    const form = new FormData();
    form.append('template', opts.template);
    for (const f of files) form.append('files', f, relName(f));
    form.append('title', opts.title);
    form.append('slides', String(opts.slides));
    form.append('cover', opts.cover);
    if (opts.guide) form.append('guide', opts.guide);
    return apiClient.upload<DraftStart>('/deck/draft', form, { timeoutMs: 180_000 });
  },

  draftStatus: (id: string) => apiClient.get<DraftStatus>(`/deck/draft/${encodeURIComponent(id)}`),

  /** PPTX — 그림은 `img:<장>:<칸키>` */
  build: (template: TemplateId, title: string, slides: DeckOut[], images: DeckPic[]) =>
    apiClient.uploadBlob('/deck/export', deckForm(template, title, slides, images)),

  /** 미리보기 PDF — 입력은 build 와 같다. 서버(grk-deck)가 PPTX 를 만들어 PDF 로 바꾼다 */
  preview: (template: TemplateId, title: string, slides: DeckOut[], images: DeckPic[]) =>
    apiClient.uploadBlob('/deck/preview', deckForm(template, title, slides, images), { timeoutMs: 180_000 }),

  save,
};
